import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../hooks/useAuth';
import { Loader2, ArrowLeft, Eye, EyeOff, CheckCircle2, Key } from 'lucide-react';
import logo from '../assets/images/logo.png';

const ResetPassword = () => {
  const { toast } = useToast();
  const { resetPassword } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Pre-fill from URL params if available
  const [formData, setFormData] = useState({
    uid: searchParams.get('uid') || '',
    token: searchParams.get('token') || '',
    new_password: '',
    new_password_confirm: ''
  });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // Password strength checker
  const getPasswordStrength = (password) => {
    if (!password) return { strength: 0, text: '', color: '' };
    
    let strength = 0;
    if (password.length >= 8) strength++;
    if (/[A-Z]/.test(password)) strength++;
    if (/[a-z]/.test(password)) strength++;
    if (/[0-9]/.test(password)) strength++;
    if (/[^A-Za-z0-9]/.test(password)) strength++;
    
    const levels = [
      { text: 'Very Weak', color: 'bg-red-500' },
      { text: 'Weak', color: 'bg-orange-500' },
      { text: 'Fair', color: 'bg-yellow-500' },
      { text: 'Good', color: 'bg-lime-500' },
      { text: 'Strong', color: 'bg-green-500' },
    ];
    
    return { strength, ...levels[strength - 1] || levels[0] };
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.uid.trim()) {
      newErrors.uid = 'User ID is required';
    }
    
    if (!formData.token.trim()) {
      newErrors.token = 'Reset token is required';
    }
    
    if (!formData.new_password) {
      newErrors.new_password = 'New password is required';
    } else if (formData.new_password.length < 8) {
      newErrors.new_password = 'Password must be at least 8 characters';
    }
    
    if (!formData.new_password_confirm) {
      newErrors.new_password_confirm = 'Please confirm your password';
    } else if (formData.new_password !== formData.new_password_confirm) {
      newErrors.new_password_confirm = 'Passwords do not match';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setLoading(true);
    
    const result = await resetPassword({
      uid: formData.uid.trim(),
      token: formData.token.trim(),
      new_password: formData.new_password,
      new_password_confirm: formData.new_password_confirm
    });
    
    setLoading(false);
    
    if (result.success) {
      setSuccess(true);
      toast.success({
        title: 'Password Reset Successful!',
        description: 'You can now sign in with your new password'
      });
    } else {
      toast.error({
        title: 'Reset Failed',
        description: result.error || 'Invalid or expired reset token'
      });
    }
  };

  const passwordStrength = getPasswordStrength(formData.new_password);

  // Success state
  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-yellow-50 flex items-center justify-center py-12 px-4">
        <div className="max-w-md w-full">
          {/* Logo */}
          <div className="text-center mb-8">
            <div className="flex justify-center mb-6">
              <div className="bg-white rounded-full p-3 shadow-lg">
                <img 
                  src={logo} 
                  alt="Climb Capital Logo" 
                  className="w-20 h-20 object-contain"
                />
              </div>
            </div>
          </div>

          {/* Success Card */}
          <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="h-8 w-8 text-green-600" />
            </div>
            
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Password Reset Complete!</h1>
            <p className="text-gray-600 mb-6">
              Your password has been successfully reset. You can now sign in with your new password.
            </p>

            <Link
              to="/login"
              className="block w-full bg-gradient-to-r from-yellow-400 to-yellow-500 text-gray-900 py-3 rounded-lg font-bold hover:from-yellow-500 hover:to-yellow-600 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 text-center"
            >
              Sign In Now
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-yellow-50 flex items-center justify-center py-12 px-4">
      <div className="max-w-md w-full">
        {/* Logo and Title */}
        <div className="text-center mb-8">
          <div className="flex justify-center mb-6">
            <div className="bg-white rounded-full p-3 shadow-lg">
              <img 
                src={logo} 
                alt="Climb Capital Logo" 
                className="w-20 h-20 object-contain"
              />
            </div>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Reset Password</h1>
          <p className="text-gray-600">
            Enter the reset code from your email and choose a new password.
          </p>
        </div>

        {/* Reset Password Form */}
        <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* User ID */}
            <div>
              <label className="block text-sm font-semibold mb-2 text-gray-700">
                User ID *
              </label>
              <input
                type="text"
                name="uid"
                value={formData.uid}
                onChange={handleChange}
                className={`w-full border-2 rounded-lg px-4 py-3 font-mono text-sm focus:outline-none transition-colors ${
                  errors.uid 
                    ? 'border-red-400 focus:border-red-500' 
                    : 'border-gray-200 focus:border-yellow-400'
                }`}
                placeholder="Enter the User ID from email"
              />
              {errors.uid && (
                <p className="mt-1 text-sm text-red-500">{errors.uid}</p>
              )}
            </div>

            {/* Reset Token */}
            <div>
              <label className="block text-sm font-semibold mb-2 text-gray-700">
                Reset Token *
              </label>
              <div className="relative">
                <Key className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                <input
                  type="text"
                  name="token"
                  value={formData.token}
                  onChange={handleChange}
                  className={`w-full border-2 rounded-lg pl-12 pr-4 py-3 font-mono text-sm focus:outline-none transition-colors ${
                    errors.token 
                      ? 'border-red-400 focus:border-red-500' 
                      : 'border-gray-200 focus:border-yellow-400'
                  }`}
                  placeholder="Enter the Reset Token from email"
                />
              </div>
              {errors.token && (
                <p className="mt-1 text-sm text-red-500">{errors.token}</p>
              )}
            </div>

            <hr className="my-4" />

            {/* New Password */}
            <div>
              <label className="block text-sm font-semibold mb-2 text-gray-700">
                New Password *
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="new_password"
                  value={formData.new_password}
                  onChange={handleChange}
                  className={`w-full border-2 rounded-lg px-4 py-3 pr-12 focus:outline-none transition-colors ${
                    errors.new_password 
                      ? 'border-red-400 focus:border-red-500' 
                      : 'border-gray-200 focus:border-yellow-400'
                  }`}
                  placeholder="Enter new password"
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              {errors.new_password && (
                <p className="mt-1 text-sm text-red-500">{errors.new_password}</p>
              )}
              {/* Password strength indicator */}
              {formData.new_password && (
                <div className="mt-2">
                  <div className="flex gap-1 mb-1">
                    {[1, 2, 3, 4, 5].map((level) => (
                      <div
                        key={level}
                        className={`h-1 flex-1 rounded-full ${
                          level <= passwordStrength.strength
                            ? passwordStrength.color
                            : 'bg-gray-200'
                        }`}
                      />
                    ))}
                  </div>
                  <p className={`text-xs ${passwordStrength.strength >= 4 ? 'text-green-600' : 'text-gray-500'}`}>
                    {passwordStrength.text}
                  </p>
                </div>
              )}
            </div>

            {/* Confirm New Password */}
            <div>
              <label className="block text-sm font-semibold mb-2 text-gray-700">
                Confirm New Password *
              </label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  name="new_password_confirm"
                  value={formData.new_password_confirm}
                  onChange={handleChange}
                  className={`w-full border-2 rounded-lg px-4 py-3 pr-12 focus:outline-none transition-colors ${
                    errors.new_password_confirm 
                      ? 'border-red-400 focus:border-red-500' 
                      : formData.new_password_confirm && formData.new_password === formData.new_password_confirm
                        ? 'border-green-400 focus:border-green-500'
                        : 'border-gray-200 focus:border-yellow-400'
                  }`}
                  placeholder="Confirm new password"
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
                {formData.new_password_confirm && formData.new_password === formData.new_password_confirm && (
                  <CheckCircle2 className="absolute right-10 top-1/2 -translate-y-1/2 text-green-500" size={20} />
                )}
              </div>
              {errors.new_password_confirm && (
                <p className="mt-1 text-sm text-red-500">{errors.new_password_confirm}</p>
              )}
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-yellow-400 to-yellow-500 text-gray-900 py-3 rounded-lg font-bold hover:from-yellow-500 hover:to-yellow-600 transition-all disabled:opacity-50 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  Resetting Password...
                </>
              ) : (
                'Reset Password'
              )}
            </button>
          </form>
        </div>

        {/* Back Links */}
        <div className="mt-6 flex flex-col items-center gap-2">
          <Link 
            to="/forgot-password" 
            className="text-gray-500 hover:text-gray-700 text-sm"
          >
            Didn't receive the email? Request again
          </Link>
          <Link 
            to="/login" 
            className="text-blue-600 hover:text-blue-800 font-semibold flex items-center gap-2"
          >
            <ArrowLeft size={18} />
            Back to Sign In
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
